import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { ADD_SHIFT_OPTIONS } from 'modules/shift_management/lib/addShiftOptions'
import buildShiftsVisibility from 'modules/shift_management/lib/buildShiftsVisibility'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import { trackEditUnassignedShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import { ShiftsRow } from 'modules/shift_management/lib/types'
import type UnassignedShift from 'modules/shift_management/store/internal/models/UnassignedShift'
import UnassignedShiftProp, {
  UnassignedShiftPayload,
} from 'modules/shift_management/store/internal/models/UnassignedShift'

import { useShiftsRows } from './useShiftsRows'
import { useUnassignedShifts } from './useUnassignedShifts'

export interface Props {
  unassignedShiftForm: UnassignedShiftForm
  unassignedShift: UnassignedShift
  modifiedShiftDetails: boolean
  contractVersions: ContractVersions
}

const trackEvent = (
  unassignedShift: UnassignedShift,
  unassignedShiftForm: UnassignedShiftForm,
  teamsIdsVisibilities: number[],
  locationsIdsVisibilities: number[]
) => {
  const shiftType = unassignedShift.isDefault
    ? ADD_SHIFT_OPTIONS.defaultShift
    : ADD_SHIFT_OPTIONS.customShift
  const counter = unassignedShiftForm.get('counter').value
  const startAt = unassignedShiftForm.get('start_at').value
  const endAt = unassignedShiftForm.get('end_at').value
  const extraHours = unassignedShiftForm.get('extra_hours').value
  const locationId = unassignedShiftForm.get('location_id').value
  const workAreaId = unassignedShiftForm.get('work_area_id').value
  const notes = unassignedShiftForm.get('notes').value
  const allCompany = unassignedShiftForm.get('all_company').value

  const extras = {
    shift_start_time: startAt,
    shift_end_time: endAt,
    overtime_flag: !!extraHours,
    location_id: locationId,
    work_area_id: workAreaId,
    notes: notes,
  }

  trackEditUnassignedShiftSubmit(
    unassignedShift,
    shiftType,
    counter,
    allCompany,
    teamsIdsVisibilities,
    locationsIdsVisibilities,
    extras
  )
}

const useEditUnassignedShifts = ({
  unassignedShiftForm,
  unassignedShift,
  modifiedShiftDetails,
  contractVersions,
}: Props) => {
  const [, dispatchUnassignedShifts] = useUnassignedShifts()
  const [{ shiftsRows }] = useShiftsRows()
  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)

  const unassignedShiftModel = unassignedShift.toPayload()

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const dateString = dateToString(
        stringToDate(unassignedShiftModel.attributes.start_at, 'backend_datetime'),
        'backend'
      )

      const startTime = unassignedShiftForm.get('start_at').value
      const endTime = unassignedShiftForm.get('end_at').value

      // startTime and endTime comes like HH:mm
      const hourStart = Number(startTime.split(':')[0])
      const hourEnd = Number(endTime.split(':')[0])

      const dateEnd = stringToDate(unassignedShiftModel.attributes.end_at, 'backend_datetime')
      // If the shift was not overnight but the endTime change to next day it is necessary add 1 day to dateEnd
      const dateEndString = dateToString(
        !unassignedShift.overnightShift && hourStart > hourEnd ? dateEnd.add(1, 'days') : dateEnd,
        'backend'
      )

      const { teamsIdsVisibilities, locationsIdsVisibilities } = buildShiftsVisibility({
        form: unassignedShiftForm,
        isSingleSelector: true,
      })

      const counter = unassignedShiftForm.get('staff_requirements').value[0].staff_count
      const jobCatalogRolesIds =
        unassignedShiftForm.get('staff_requirements').value[0].job_catalog_roles_ids

      //It's needed because start_at and end_at have a format like 2023-09-04 13:00
      unassignedShiftForm.setValues({
        start_at: dateString + ' ' + startTime,
        end_at: dateEndString + ' ' + endTime,
        teams_ids_visibilities: teamsIdsVisibilities,
        locations_ids_visibilities: locationsIdsVisibilities,
        counter: counter,
        job_catalog_roles_ids: jobCatalogRolesIds,
      })

      trackEvent(
        unassignedShift,
        unassignedShiftForm,
        teamsIdsVisibilities,
        locationsIdsVisibilities
      )

      const result = await asyncAction(() => unassignedShiftForm.save(unassignedShiftModel), {
        successMessage: i18n.t('shift_management.actions.edit_unassigned_shift_success'),
      })

      if (isSuccessful(result)) {
        const { value }: { value: UnassignedShiftPayload } = result
        const unassignedShiftResponse = UnassignedShiftProp.buildFromProps(value)

        dispatchUnassignedShifts({
          type: 'EDIT_UNASSIGNED_SHIFT',
          unassignedShift: unassignedShiftResponse,
        })

        if (modifiedShiftDetails) {
          const relatedShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
            return row.shifts.filterByUnassignedShift(unassignedShift.id)
          })
          await refreshShiftsRows(relatedShiftsRows, []) // Should we pick the related shifts rows inside this hook instead of passing it as argument?
        }

        closePortal()
      }
    },
    {
      successFlash: false,
      errorFlash: false,
    }
  )

  return {
    submitting,
    onSubmit,
  }
}

export { useEditUnassignedShifts }
